<template>
  <FlashNotifications ref="flash" />

  <div class="flex flex-col h-app-screen pb-14 md:pb-0 bg-gray-50 dark:bg-[#0d1117]">
    <header class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      <div class="flex items-center gap-3 px-4 sm:px-6 py-2.5">
        <h1 class="text-lg font-bold tracking-tight text-gray-900 dark:text-white leading-none">CV Builder</h1>
        <span v-if="store.fetchedAt" class="hidden sm:inline text-xs text-gray-400 dark:text-gray-500 tabular-nums">{{ countLabel }}</span>

        <HelpLink path="job-tracker/create-your-default-resume" label="Guide" class="hidden md:inline-flex flex-shrink-0" />

        <div v-if="store.resumes.length" class="flex-1 min-w-0 max-w-xl mx-auto">
          <ResumeSearch v-model="query" :resumes="store.resumes" />
        </div>

        <div v-if="store.resumes.length" role="radiogroup" aria-label="View" class="ml-auto sm:ml-0 inline-flex p-0.5 rounded-lg bg-gray-100 dark:bg-gray-800">
          <button v-for="option in VIEWS" :key="option.id" type="button" role="radio" :aria-checked="view === option.id" :title="option.label" :aria-label="option.label" @click="setView(option.id)"
            class="w-8 h-7 inline-flex items-center justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
            :class="view === option.id ? 'bg-white dark:bg-[#1C2128] text-gray-900 dark:text-white shadow-sm' : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" :d="option.icon"/></svg>
          </button>
        </div>

        <button type="button" @click="openCreate" :disabled="!store.fetchedAt"
          class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-[13px] font-semibold text-white shadow-sm disabled:opacity-60 disabled:cursor-not-allowed">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
          New resume
        </button>
      </div>
    </header>

    <div class="flex-1 min-h-0 overflow-y-auto">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 py-5">
        <!-- Loading: as many placeholders as there were last time -->
        <div v-if="!store.fetchedAt && store.loading" :class="GRID" class="animate-pulse" aria-hidden="true">
          <div v-for="n in skeletonCount" :key="n" class="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128]">
            <div class="p-3 pb-0"><div class="rounded-lg bg-gray-100 dark:bg-gray-800 aspect-[794/1123]"></div></div>
            <div class="px-3.5 pt-3 pb-3.5 space-y-2">
              <div class="h-3.5 w-3/5 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="h-3 w-4/5 rounded bg-gray-100 dark:bg-gray-800"></div>
              <div class="h-2.5 w-2/5 rounded bg-gray-100 dark:bg-gray-800"></div>
            </div>
          </div>
        </div>

        <!-- Error -->
        <div v-else-if="store.error && !store.fetchedAt" class="flex flex-col items-center justify-center py-24 text-center px-4">
          <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
          </div>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
          <button type="button" @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Try again</button>
        </div>

        <!-- Nothing yet -->
        <div v-else-if="store.resumes.length === 0" class="max-w-md mx-auto text-center py-16">
          <div class="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mx-auto mb-5">
            <svg class="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>
          </div>
          <h2 class="text-lg font-bold text-gray-900 dark:text-white">Build your first resume</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">Fill in your details once. It becomes your default resume, and every new one starts from it.</p>
          <button type="button" @click="openCreate" class="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm">Create your resume</button>
        </div>

        <!-- The resumes -->
        <!-- A search that finds nothing -->
        <div v-else-if="shown.length === 0" class="max-w-md mx-auto text-center py-16">
          <p class="text-base font-semibold text-gray-900 dark:text-white">No resumes match "{{ query }}"</p>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">Try a job title, a company or a skill.</p>
          <button type="button" @click="query = ''" class="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">Clear search</button>
        </div>

        <ResumeTable v-else-if="view === 'table'" :resumes="shown"
          @edit-details="openEdit" @make-default="makeDefault" @duplicate="duplicate" @delete="deleting = $event" />

        <ul v-else :class="GRID">
          <li v-for="resume in shown" :key="resume.id">
            <ResumeCard :resume="resume"
              @edit-details="openEdit" @make-default="makeDefault" @duplicate="duplicate" @delete="deleting = $event" />
          </li>
        </ul>
      </div>
    </div>

    <ResumeFormDialog :open="dialog.open" :mode="dialog.mode" :resume="dialog.resume" :has-default="Boolean(store.defaultResume)"
      :saving="dialog.saving" :error="dialog.error" @submit="submitDialog" @cancel="dialog.open = false" />

    <ConfirmModal :open="Boolean(deleting)" title="Delete this resume?" confirm-text="Delete resume" :saving="deletingNow"
      :message="deleting?.is_default ? 'This is your default resume. Your most recently edited one becomes the default.' : 'This cannot be undone.'"
      @cancel="deleting = null" @confirm="confirmDelete">
      <template #subject>
        <span v-if="deleting" class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ deleting.name }}</span>
      </template>
    </ConfirmModal>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useResumesStore } from 'stores/resumes.js'
import { getCachedResumesCount } from 'composables/useResumesCountCache.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import ConfirmModal from 'controllers/vue_components/shared/ConfirmModal.vue'
import ResumeCard from './ResumeCard.vue'
import ResumeTable from './ResumeTable.vue'
import ResumeSearch from './ResumeSearch.vue'
import HelpLink from 'controllers/vue_components/shared/HelpLink.vue'
import { filterResumes } from './resumeSearch.js'
import ResumeFormDialog from './ResumeFormDialog.vue'

const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4'

const VIEWS = [
  { id: 'cards', label: 'Card view', icon: 'M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z' },
  { id: 'table', label: 'Table view', icon: 'M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5' },
]
const VIEW_KEY = 'resumes.view'

// Cards by default; the choice is remembered in this browser.
const readView = () => {
  try { return localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'cards' } catch { return 'cards' }
}
const view = ref(readView())
function setView(id) {
  view.value = id
  try { localStorage.setItem(VIEW_KEY, id) } catch { /* private window: the choice lasts until the page is closed */ }
}

const store = useResumesStore()
const query = ref('')
const shown = computed(() => filterResumes(store.resumes, query.value))
const countLabel = computed(() => {
  const total = store.resumes.length
  const noun = (n) => `${n} ${n === 1 ? 'resume' : 'resumes'}`
  return query.value.trim() ? `${shown.value.length} of ${noun(total)}` : noun(total)
})
const router = useRouter()
const flash = ref(null)
const skeletonCount = getCachedResumesCount()
const deleting = ref(null)
const deletingNow = ref(false)
const dialog = reactive({ open: false, mode: 'create', resume: null, saving: false, error: '' })

const notify = (message, type = 'notice') => flash.value?.show(message, type)

function openCreate() {
  Object.assign(dialog, { open: true, mode: 'create', resume: null, error: '' })
}

function openEdit(resume) {
  Object.assign(dialog, { open: true, mode: 'edit', resume, error: '' })
}

async function submitDialog(attrs) {
  dialog.saving = true
  dialog.error = ''
  const result = dialog.mode === 'create' ? await store.create(attrs) : await store.update(dialog.resume.id, attrs)
  dialog.saving = false

  if (!result.ok) {
    dialog.error = result.message
    return
  }
  dialog.open = false
  if (dialog.mode === 'create') {
    router.push(`/cv_builder/${result.resume.id}`) // straight into the editor
    return
  }
  notify('Resume saved')
}

async function makeDefault(resume) {
  const result = await store.makeDefault(resume.id)
  if (result.ok) notify(`${resume.name} is now your default resume`)
  else notify(result.message, 'alert')
}

async function duplicate(resume) {
  const result = await store.duplicate(resume.id)
  if (result.ok) notify(`Copied as ${result.resume.name}`)
  else notify(result.message, 'alert')
}

async function confirmDelete() {
  const resume = deleting.value
  deletingNow.value = true
  const result = await store.remove(resume.id)
  deletingNow.value = false
  deleting.value = null
  if (result.ok) notify('Resume deleted')
  else notify(result.message, 'alert')
}

const retry = () => store.fetchResumes()

onMounted(() => store.fetchIfStale())
</script>
